import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { Fieldset } from '@repo/design-system/components/ui/fieldset';
import { Form } from '@repo/design-system/components/ui/form';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useT } from '@repo/i18n/components/i18n.client';
import { useCreateMapCategoryMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapCategories } from '@repo/store/maps/slices/mapData';
import { getRandomAvailableColor } from '@repo/store/maps/utils/categoryColors';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import { ColorPicker } from '@repo/ui/components/color-picker/color-picker';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CategoryColorPalette } from './category-color-palette';
interface CategoryFormState {
  categoryName: string;
  displayOrder: string;
  selectedColor: string;
  description: string;
  visibleByDefault: boolean;
  excludeFromChecklists: boolean;
}
const initialFormState: CategoryFormState = {
  categoryName: '',
  displayOrder: '',
  selectedColor: DEFAULT_PIN_COLOR,
  description: '',
  visibleByDefault: false,
  excludeFromChecklists: false
};
export const AddCategoryForm = () => {
  const {
    t
  } = useT(['game_map']);
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapCategories = useAppSelector(selectMapCategories);
  const [createCategory, {
    isLoading
  }] = useCreateMapCategoryMutation();
  const [formState, setFormState] = useState<CategoryFormState>(initialFormState);
  const existingColors = useMemo(() => (mapCategories || []).map(cat => cat.color), [mapCategories]);
  const updateFormState = useCallback((updates: Partial<CategoryFormState>) => {
    setFormState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const handleCancel = useCallback(() => {
    setFormState(initialFormState);
  }, []);
  useEffect(() => {
    const nextColor = getRandomAvailableColor(existingColors);
    updateFormState({
      selectedColor: nextColor
    });
  }, [existingColors, updateFormState]);
  const handleSave = useCallback(async () => {
    const {
      categoryName,
      displayOrder,
      selectedColor,
      visibleByDefault,
      description,
      excludeFromChecklists
    } = formState;
    if (!categoryName.trim() || !externalMapId) return;
    try {
      await createCategory({
        mapExternalId: externalMapId,
        category: {
          title: categoryName,
          displayOrder: displayOrder === '' ? null : Number(displayOrder),
          color: selectedColor,
          visibleByDefault,
          description,
          excludeFromChecklists
        }
      }).unwrap();
      setFormState({
        ...initialFormState,
        selectedColor: getRandomAvailableColor([...existingColors, selectedColor])
      });
      toast({
        title: t($_1 => $_1.add_category),
        description: t($_2 => $_2.category_added_successfully),
        variant: 'success'
      });
    } catch {
      toast({
        title: t($ => $.add_category),
        description: t($_0 => $_0.failed_to_create_category),
        variant: 'error'
      });
    }
  }, [formState, externalMapId, createCategory, existingColors, t]);
  const displayOrderNumber = Number(formState.displayOrder);
  const hasDisplayOrder = formState.displayOrder !== '';
  const hasValidDisplayOrder = !hasDisplayOrder || Number.isInteger(displayOrderNumber) && displayOrderNumber >= 0;
  const hasDuplicateDisplayOrder = hasDisplayOrder && mapCategories?.some(category => category.displayOrder === displayOrderNumber);
  const displayOrderError = !hasValidDisplayOrder ? 'Enter a whole number greater than or equal to 0.' : hasDuplicateDisplayOrder ? 'Another category in this map already uses that display order.' : undefined;
  const isFormValid = formState.categoryName.trim() && hasValidDisplayOrder && !hasDuplicateDisplayOrder;
  return <Form spacing="md" onSubmit={e => {
    e.preventDefault();
    void handleSave();
  }}>
            <Fieldset legend={t($_3 => $_3.category_details)} legendHidden spacing="md">
                <TextInput fullWidth label={t($_4 => $_4.name)} value={formState.categoryName} onChange={e_0 => updateFormState({
        categoryName: e_0.target.value
      })} placeholder={t($_5 => $_5.category_name)} size="sm" required />

                <TextInput fullWidth label="Display order" value={formState.displayOrder} onChange={e_1 => updateFormState({
        displayOrder: e_1.target.value
      })} placeholder="Optional" type="number" min={0} step={1} size="sm" errorText={displayOrderError} helperText="Lower numbers appear first. Blank categories sort alphabetically after ordered categories." />

                <FlexColumn gap="8">
                    <ColorPicker label={t($_6 => $_6.color)} value={formState.selectedColor} onChange={color => updateFormState({
          selectedColor: color
        })} size="sm" />
                    <CategoryColorPalette selectedColor={formState.selectedColor} onColorSelect={color_0 => updateFormState({
          selectedColor: color_0
        })} label="Choose from palette" />
                </FlexColumn>

                <TextArea fullWidth size="sm" label={t($_7 => $_7.description)} value={formState.description || ''} onChange={e_2 => updateFormState({
        description: e_2.target.value
      })} placeholder="Max 500 characters" maxLength={500} helperText={`${formState.description.length}/500`} />

                <Checkbox id="visible-by-default" label={t($_8 => $_8.visible_by_default)} checked={formState.visibleByDefault} onCheckedChange={details => updateFormState({
        visibleByDefault: !!details.checked
      })} size="sm" />

                <Checkbox id="exclude-category-from-checklists" label="Exclude category from checklists" checked={formState.excludeFromChecklists} onCheckedChange={details_0 => updateFormState({
        excludeFromChecklists: !!details_0.checked
      })} size="sm" />
            </Fieldset>

            <FlexRow gap="4" justify="end">
                <Button variant="ghost" onClick={handleCancel} color="secondary" size="sm">
                    {t($_9 => $_9.clear)}
                </Button>
                <Button type="submit" color="success" size="sm" disabled={!isFormValid || isLoading}>
                    {isLoading ? t($_10 => $_10.adding) : t($_11 => $_11.add_category)}
                </Button>
            </FlexRow>
        </Form>;
};